import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, SlidersHorizontal, X, LayoutGrid, List, Search } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ProductTableView from '../components/ProductTableView';
import { CatalogAPI } from '../lib/api';
import { Checkbox } from '../components/ui/checkbox';

const Skeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="bg-white border border-[#E8E0D0] rounded-xl overflow-hidden animate-pulse">
        <div className="aspect-square bg-[#F5F1EA]" />
        <div className="p-4 space-y-2">
          <div className="h-3 bg-[#F5F1EA] rounded w-1/3" />
          <div className="h-4 bg-[#F5F1EA] rounded w-5/6" />
          <div className="h-4 bg-[#F5F1EA] rounded w-2/3" />
        </div>
      </div>
    ))}
  </div>
);

const CategoryPage = ({ onAddToCart }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q');

  const [categories,    setCategories]    = useState([]);
  const [brands,        setBrands]        = useState([]);
  const [products,      setProducts]      = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [sortBy,        setSortBy]        = useState('popular');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [availability,  setAvailability]  = useState('all'); // 'all' | 'in_stock'
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('dz_view_mode') === 'table' ? 'table' : 'grid'; }
    catch { return 'grid'; }
  });
  const toggleView = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('dz_view_mode', mode); } catch { /* localStorage disabled */ }
  };

  const category = categories.find((c) => c.id === id);
  const parent = category?.parent_id ? categories.find((c) => c.id === category.parent_id) : null;
  const children = categories.filter((c) => c.parent_id === id);
  const siblings = category?.parent_id
    ? categories.filter((c) => c.parent_id === category.parent_id)
    : [];
  // Famille racine (niveau 1) de la catégorie active
  const rootFamily = (() => {
    if (!category) return null;
    if (category.level === 1) return category;
    if (parent && parent.level === 1) return parent;
    if (parent && parent.parent_id) return categories.find((c) => c.id === parent.parent_id);
    return null;
  })();
  const families = categories.filter((c) => c.level === 1).sort((a,b)=>(a.order||0)-(b.order||0));
  const catsOf = (pid) => categories.filter((c) => c.parent_id === pid).sort((a,b)=>(a.order||0)-(b.order||0));

  useEffect(() => {
    CatalogAPI.categories().then(setCategories);
    CatalogAPI.brands().then(setBrands);
  }, []);

  // Reset filtres quand catégorie/recherche change
  useEffect(() => { setSelectedBrands([]); setAvailability('all'); }, [id, q]);

  useEffect(() => {
    setLoading(true);
    const params = { sort: sortBy };
    if (id) params.category = id;
    if (q)  params.q = q;
    if (availability === 'in_stock') params.in_stock = true;
    CatalogAPI.products(params)
      .then((data) => {
        setProducts(Array.isArray(data) ? data : (data?.items || []));
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [id, q, sortBy, availability]);

  const filtered = useMemo(() => {
    if (!selectedBrands.length) return products;
    return products.filter((p) => selectedBrands.includes(p.brand));
  }, [products, selectedBrands]);

  const toggleBrand = (b) =>
    setSelectedBrands((prev) => prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b]);

  const hasActiveFilters = selectedBrands.length > 0 || availability !== 'all';
  const resetFilters = () => { setSelectedBrands([]); setAvailability('all'); };

  const title = q ? `Résultats pour « ${q} »` : (category?.name || 'Catalogue');

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4 flex-wrap">
        <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to="/categories" className="hover:text-[#1F4D3F]">Catégories</Link>
        {parent && (
          <>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to={`/categorie/${parent.id}`} className="hover:text-[#1F4D3F]">{parent.name}</Link>
          </>
        )}
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#2D2A26]">{title}</span>
      </nav>

      <div className="mb-6">
        <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F]">{title}</h1>
        <p className="text-[#7A6F5C] mt-2">
          {loading ? 'Chargement...' : `${filtered.length} produit${filtered.length !== 1 ? 's' : ''} · prix comparés entre fournisseurs algériens`}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="bg-white border border-[#E8E0D0] rounded-2xl p-5 h-fit lg:sticky lg:top-[180px]">
          {/* Navigation hiérarchique 3 niveaux */}
          {families.length > 0 && (
            <div className="mb-5 pb-5 border-b border-[#F5F1EA]" data-testid="subcategories-nav">
              <div className="text-sm font-semibold text-[#2D2A26] mb-3">Catégories</div>
              <div className="space-y-0.5">
                {families.map((fam) => {
                  const famCats = catsOf(fam.id);
                  const isActiveFam = rootFamily?.id === fam.id;
                  return (
                    <div key={fam.id}>
                      <Link
                        to={famCats.length ? `/categorie/${famCats[0].id}` : `/categorie/${fam.id}`}
                        className={`flex items-center justify-between text-sm rounded-md px-2 py-1.5 font-medium transition-colors ${
                          isActiveFam ? 'text-[#1F4D3F]' : 'text-[#2D2A26] hover:bg-[#F5F1EA]'
                        }`}>
                        <span className="truncate">{fam.name}</span>
                        <span className="text-xs text-[#7A6F5C]">{isActiveFam ? '▾' : '▸'}</span>
                      </Link>
                      {isActiveFam && (
                        <div className="ml-2 pl-2 border-l border-[#E8E0D0] space-y-0.5 mt-0.5">
                          {famCats.map((c) => {
                            const subs = catsOf(c.id);
                            const isActiveCat = c.id === id || subs.some((s) => s.id === id);
                            return (
                              <div key={c.id}>
                                <Link
                                  to={`/categorie/${c.id}`}
                                  className={`flex items-center justify-between text-sm rounded-md px-2 py-1.5 transition-colors ${
                                    c.id === id ? 'bg-[#1F4D3F] text-white font-medium'
                                    : isActiveCat ? 'text-[#1F4D3F] font-medium' : 'text-[#5A5247] hover:bg-[#F5F1EA] hover:text-[#1F4D3F]'
                                  }`}>
                                  <span className="truncate">{c.name}</span>
                                  <span className={`text-xs ml-2 ${c.id === id ? 'text-white/70' : 'text-[#7A6F5C]'}`}>{c.products_count || 0}</span>
                                </Link>
                                {isActiveCat && subs.length > 0 && (
                                  <div className="ml-2 pl-2 border-l border-[#F0E9DC] space-y-0.5 mt-0.5">
                                    {subs.map((s) => (
                                      <Link
                                        key={s.id}
                                        to={`/categorie/${s.id}`}
                                        className={`flex items-center justify-between text-[13px] rounded-md px-2 py-1 transition-colors ${
                                          s.id === id ? 'bg-[#2D6452] text-white font-medium' : 'text-[#7A6F5C] hover:bg-[#F5F1EA] hover:text-[#1F4D3F]'
                                        }`}>
                                        <span className="truncate">{s.name}</span>
                                        <span className={`text-xs ml-2 ${s.id === id ? 'text-white/70' : 'text-[#9A8F7C]'}`}>{s.products_count || 0}</span>
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <SlidersHorizontal className="w-4 h-4 text-[#1F4D3F]" />
            <h3 className="font-medium text-[#2D2A26]">Filtres</h3>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                data-testid="filters-reset-btn"
                className="ml-auto text-xs text-[#C75B27] hover:underline flex items-center gap-1">
                <X className="w-3 h-3" /> Réinitialiser
              </button>
            )}
          </div>

          {/* Filtre Disponibilité (style AERA) */}
          <div className="mb-6" data-testid="availability-filter">
            <div className="text-sm font-medium text-[#2D2A26] mb-2">Disponibilité</div>
            <div className="space-y-1">
              {[
                { v: 'all',      label: 'Tous les produits' },
                { v: 'in_stock', label: 'En stock uniquement' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setAvailability(opt.v)}
                  data-testid={`availability-${opt.v}`}
                  className={`w-full text-left text-sm rounded-md px-2.5 py-1.5 transition-colors ${
                    availability === opt.v
                      ? 'bg-[#1F4D3F] text-white font-medium'
                      : 'text-[#5A5247] hover:bg-[#F5F1EA] hover:text-[#1F4D3F]'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-[#2D2A26] mb-3">Marques</div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
              {brands.slice(0, 14).map((b) => (
                <label key={b} className="flex items-center gap-2 cursor-pointer text-sm text-[#5A5247]">
                  <Checkbox checked={selectedBrands.includes(b)} onCheckedChange={() => toggleBrand(b)}
                    data-testid={`brand-checkbox-${b}`}
                    className="data-[state=checked]:bg-[#1F4D3F] data-[state=checked]:border-[#1F4D3F]" />
                  <span>{b}</span>
                </label>
              ))}
            </div>
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-4 bg-white border border-[#E8E0D0] rounded-xl px-4 py-3 gap-3 flex-wrap">
            <div className="text-sm text-[#7A6F5C]">
              {loading ? '…' : <><span className="font-medium text-[#2D2A26]">{filtered.length}</span> résultat{filtered.length !== 1 ? 's' : ''}</>}
            </div>
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-md border border-[#E8E0D0] overflow-hidden" data-testid="view-mode-toggle">
                <button
                  onClick={() => toggleView('grid')}
                  data-testid="view-mode-grid"
                  title="Vue grille"
                  className={`p-1.5 ${viewMode === 'grid' ? 'bg-[#1F4D3F] text-white' : 'bg-white text-[#7A6F5C] hover:bg-[#F5F1EA]'}`}>
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => toggleView('table')}
                  data-testid="view-mode-table"
                  title="Vue tableau"
                  className={`p-1.5 ${viewMode === 'table' ? 'bg-[#1F4D3F] text-white' : 'bg-white text-[#7A6F5C] hover:bg-[#F5F1EA]'}`}>
                  <List className="w-4 h-4" />
                </button>
              </div>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                data-testid="sort-select"
                className="text-sm border border-[#E8E0D0] rounded-md px-3 py-1.5 bg-white text-[#2D2A26] focus:outline-none focus:ring-2 focus:ring-[#1F4D3F]">
                <option value="popular">Plus populaires</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
                <option value="discount">Meilleures réductions</option>
              </select>
            </div>
          </div>

          {loading ? <Skeleton /> : filtered.length === 0 ? (
            <div className="bg-white border border-[#E8E0D0] rounded-2xl p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-[#F5F1EA] flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-[#1F4D3F]" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-[#1F4D3F] mb-1">Aucun produit trouvé</h3>
              <p className="text-[#7A6F5C] mb-5">Essayez d'autres mots-clés, ou explorez nos catégories.</p>
              {hasActiveFilters && (
                <button onClick={resetFilters} className="mb-5 inline-block text-sm text-[#C75B27] hover:underline font-medium">
                  ✕ Effacer les filtres
                </button>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-[#9A8F82] mb-3">Explorer par catégorie</div>
                <div className="flex flex-wrap gap-2 justify-center mb-5">
                  {[
                    { label: 'Consommables', id: 'fam_omni' },
                    { label: 'Implantologie', id: 'fam_implanto' },
                    { label: 'Prothèse', id: 'fam_prothesiste' },
                    { label: 'Numérique', id: 'fam_numerique' },
                  ].map((c) => (
                    <Link key={c.id} to={`/categorie/${c.id}`}
                      className="px-4 py-2 bg-[#FAF7F2] border border-[#E8E0D0] rounded-full text-sm text-[#1F4D3F] hover:bg-white transition">
                      {c.label}
                    </Link>
                  ))}
                </div>
                <Link to="/categories" className="inline-flex items-center gap-2 bg-[#1F4D3F] hover:bg-[#173B30] text-white px-6 py-3 rounded-lg font-medium">
                  Voir tout le catalogue
                </Link>
              </div>
            </div>
          ) : viewMode === 'table' ? (
            <ProductTableView products={filtered} onAddToCart={onAddToCart} />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {filtered.map((p) => <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
